import type { GuideEdition } from './types'

export interface BackendGuideRouteRecord {
  id: string
  path: string
  menuId: string
  fullPath: string
  component: string
  name: string
  title: string
  parentTitles: string[]
  parentPaths: string[]
  parentIds: string[]
  depth: number
}

interface BackendRouteNode {
  id?: string | number
  path?: string
  component?: unknown
  name?: string
  meta?: {
    title?: unknown
    menuId?: string | number
  }
  children?: BackendRouteNode[]
}

type EditionId = GuideEdition['id']

export type GuideRouteResolutionStatus = 'matched' | 'not-found' | 'ambiguous'

export interface GuideRouteResolution {
  status: GuideRouteResolutionStatus
  record: BackendGuideRouteRecord | null
  candidates: Array<{ record: BackendGuideRouteRecord, score: number, coverage: number }>
}

function normalizePath(value: unknown) {
  const path = String(value || '')
    .split(/[?#]/)[0]
    .replace(/\\/g, '/')
    .replace(/\/{2,}/g, '/')
    .trim()
  if (!path) return ''
  const normalized = path.startsWith('/') ? path : `/${path}`
  return normalized.length > 1 ? normalized.replace(/\/$/, '') : normalized
}

function joinRoutePath(parentPath: string, childPath: unknown) {
  const child = String(childPath || '').trim()
  if (!child) return normalizePath(parentPath)
  if (child.startsWith('/')) return normalizePath(child)
  return normalizePath(`${parentPath}/${child}`)
}

/**
 * 将 routeList 接口原始树展平为新手引导路由索引。
 * path、component、name、title、menuId、id 均直接读取接口；
 * fullPath 只由接口父子 path 拼接，不读取任何本地页面配置。
 */
export function createBackendGuideRouteRecords(
  routes: BackendRouteNode[] = [],
  parentPath = '',
  parentTitles: string[] = [],
  parentPaths: string[] = [],
  parentIds: string[] = [],
): BackendGuideRouteRecord[] {
  const result: BackendGuideRouteRecord[] = []

  routes.forEach((route) => {
    const rawPath = String(route.path || '')
    const fullPath = joinRoutePath(parentPath, rawPath)
    const title = typeof route.meta?.title === 'string' ? route.meta.title : ''
    const routeId = String(route.id || '')
    const currentTitles = title ? [...parentTitles, title] : [...parentTitles]
    const currentPaths = rawPath ? [...parentPaths, rawPath] : [...parentPaths]
    const currentIds = routeId ? [...parentIds, routeId] : [...parentIds]
    const component = String(route.component || '')

    if (component && component.trim().toLowerCase() !== 'layout') {
      result.push({
        id: routeId,
        path: rawPath,
        menuId: String(route.meta?.menuId ?? route.id ?? ''),
        fullPath,
        component,
        name: String(route.name || ''),
        title,
        parentTitles: currentTitles,
        parentPaths: currentPaths,
        parentIds: currentIds,
        depth: currentPaths.length,
      })
    }

    if (route.children?.length) {
      result.push(...createBackendGuideRouteRecords(
        route.children,
        fullPath,
        currentTitles,
        currentPaths,
        currentIds,
      ))
    }
  })

  return result
}

const IGNORED_TOKENS = new Set([
  'html', 'page', 'index', 'manage', 'management', 'module', 'overview', 'list',
])

// 仅用于解释 pageKey 的通用业务同义词，不包含任何接口路由的具体值。
const TOKEN_ALIASES: Record<string, string[]> = {
  customer: ['client'],
  client: ['customer'],
  organization: ['organizational'],
  organizational: ['organization'],
  employee: ['employees', 'staff', 'pm'],
  employees: ['employee', 'staff', 'pm'],
  member: ['members', 'user', 'users', 'mement'],
  members: ['member', 'user', 'users', 'mement'],
  supplier: ['vendor'],
  finance: ['financial'],
  financial: ['finance'],
  bill: ['bills', 'settlement'],
  bills: ['bill', 'settlement'],
  settlement: ['bill', 'bills'],
  log: ['logs'],
  logs: ['log'],
  cooperation: ['cloud', 'cloudsources'],
  cloud: ['cooperation', 'cloudsources'],
  cloudsources: ['cooperation', 'cloud'],
  my: ['myinfo'],
  myinfo: ['my', 'info'],
  auxiliary: ['otherfunctions', 'other', 'functions'],
  otherfunctions: ['auxiliary'],
  dictionary: ['datadictionary', 'datadictionaryconfig'],
  datadictionary: ['dictionary', 'datadictionaryconfig'],
  datadictionaryconfig: ['dictionary', 'datadictionary'],
  ip: ['queryip'],
  queryip: ['ip'],
  uid: ['uidparsing', 'uidsorting'],
  parse: ['parsing', 'query'],
  parsing: ['parse', 'query'],
  query: ['parse', 'parsing'],
  link: ['chain', 'querychainorderid'],
  chain: ['link', 'querychainorderid'],
  querychainorderid: ['link', 'chain'],
  screen: ['questionnaire'],
  questionnaire: ['screen'],
  integration: ['integrations', 'tripleconfig'],
  integrations: ['integration', 'tripleconfig'],
  tripleconfig: ['integration', 'integrations'],
  event: ['events', 'notice', 'eventnotice'],
  events: ['event', 'notice', 'eventnotice'],
  eventnotice: ['event', 'events', 'notice'],
  settings: ['setting', 'systemsetting'],
  setting: ['settings', 'systemsetting'],
  systemsettings: ['systemsetting', 'settings'],
  systemsetting: ['systemsettings', 'setting', 'settings'],
  station: ['survey'],
  survey: ['station'],
  callback: ['callbackrecord'],
  callbackrecord: ['callback'],
  dashboard: ['board', 'datacenter', 'databoard', 'dataanalysis'],
  analysis: ['dataanalysis'],
  datacenter: ['dashboard'],
  databoard: ['dashboard'],
  dataanalysis: ['dashboard', 'analysis'],
  panel: ['panelbooks'],
  book: ['books', 'panelbooks'],
  books: ['book', 'panelbooks'],
  security: ['safety'],
  safety: ['security'],
  schedule: ['scheduling'],
  scheduling: ['schedule'],
  invoice: ['invoices'],
  invoices: ['invoice'],
  role: ['roles'],
  roles: ['role'],
  department: ['departments'],
  departments: ['department'],
  commission: ['commissions'],
  commissions: ['commission'],
  project: ['projects'],
  projects: ['project'],
  record: ['records'],
  records: ['record'],
}

function splitTokens(value: unknown) {
  return String(value || '')
    .replace(/\.html$/i, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
}

function expandToken(token: string) {
  return new Set([token, ...(TOKEN_ALIASES[token] || [])])
}

function pageKeyTokens(pageKey: string) {
  return [...new Set(
    splitTokens(pageKey).filter(token => !IGNORED_TOKENS.has(token)),
  )]
}

function routeTokenSets(record: BackendGuideRouteRecord) {
  const primary = new Set([
    ...splitTokens(record.path),
    ...splitTokens(record.fullPath),
    ...splitTokens(record.component),
    ...splitTokens(record.name),
    ...record.parentPaths.flatMap(splitTokens),
  ])
  const secondary = new Set([
    ...splitTokens(record.title),
    ...record.parentTitles.flatMap(splitTokens),
  ])
  return { primary, secondary }
}

function tokenMatches(token: string, tokenSet: Set<string>) {
  return [...expandToken(token)].some(candidate => tokenSet.has(candidate))
}

const DOMAIN_TOKENS = new Set([
  'client', 'customer', 'supplier', 'member', 'employee', 'organization',
  'project', 'finance', 'record', 'config', 'survey', 'station',
  'cooperation', 'cloud', 'auxiliary',
])

interface PageSemanticRule {
  all?: string[]
  leafAll?: string[]
  any?: string[][]
  leafAny?: string[][]
  forbid?: string[]
  leafForbid?: string[]
  preferShallow?: boolean
}

/**
 * pageKey 只描述业务语义，不保存任何接口路由字段。
 * 下面的词是“客户/项目/账单/部门”等通用业务概念，不是 path、fullPath、
 * component、name、title 或 menuId 的静态值。实际返回记录仍完全来自 routeList。
 */
const PAGE_KEY_RULES: Record<string, PageSemanticRule> = {
  'customer.list': {
    leafAll: ['client'],
    leafForbid: ['dashboard', 'board', 'invoice', 'cloud', 'cooperation'],
  },
  'supplier.list': {
    leafAll: ['supplier'],
    leafForbid: ['board', 'level', 'tenant', 'user', 'panel', 'finance', 'log', 'settlement', 'cloud', 'cooperation', 'client', 'customer'],
  },
  'supplier.member.list': {
    all: ['supplier'],
    leafAny: [['member', 'user', 'tenant']],
    leafForbid: ['finance', 'log', 'bill', 'settlement'],
  },
  'supplier.level': { all: ['supplier'], leafAll: ['level'] },
  'supplier.panel.book': { all: ['supplier'], leafAll: ['panel', 'book'] },
  'project.list': {
    leafAll: ['project', 'manage'],
    leafForbid: ['hybrid', 'schedule', 'scheduling', 'settled', 'settlement', 'quality', 'qi'],
  },
  'cooperation.cloud.customer': {
    all: ['cloud'],
    leafAll: ['customer'],
    leafForbid: ['supplier', 'my', 'info'],
  },
  'cooperation.cloud.supplier': {
    all: ['cloud'],
    leafAll: ['supplier'],
    leafForbid: ['customer', 'client', 'my', 'info'],
  },
  'cooperation.my': {
    all: ['cloud'],
    leafAny: [['my', 'myinfo']],
    leafForbid: ['supplier'],
  },
  'auxiliary.dictionary': { leafAll: ['dictionary'] },
  'auxiliary.ip.query': { leafAll: ['ip', 'query'] },
  'auxiliary.uid.parse': { leafAll: ['uid'], leafAny: [['parse', 'parsing']] },
  'auxiliary.link.parse': { leafAny: [['link', 'chain'], ['parse', 'query']] },
  'config.system.settings': { all: ['config'], leafAll: ['system', 'settings'] },
  'config.screen.library': { all: ['config'], leafAny: [['screen', 'questionnaire']] },
  'config.integrations': {
    all: ['config'],
    leafAny: [['integration', 'tripleconfig']],
    leafForbid: ['sms', 'email', 'ai'],
    preferShallow: true,
  },
  'config.integrations.sms': {
    all: ['config'],
    leafAll: ['sms'],
  },
  'config.events': { all: ['config'], leafAll: ['event'] },
  'config.security': { all: ['config'], leafAll: ['security'] },
  'config.advanced': { all: ['config'], leafAll: ['advanced'] },
  'finance.member.log': { all: ['finance'], leafAll: ['member', 'log'] },
  'finance.employee.log': { all: ['finance'], leafAll: ['employee', 'log'] },
  'finance.supplier.log': { all: ['finance'], leafAll: ['supplier', 'log'] },
  'finance.member.bill': { all: ['finance'], leafAll: ['member', 'bill'] },
  'finance.employee.bill': { all: ['finance'], leafAll: ['employee', 'bill'] },
  'finance.supplier.bill': { all: ['finance'], leafAll: ['supplier', 'bill'] },
  'finance.customer.invoice': { all: ['finance'], leafAll: ['customer', 'invoice'] },
  'organization.department': { all: ['organization'], leafAll: ['department'] },
  'organization.role': { all: ['organization'], leafAll: ['role'] },
  'organization.employee': { all: ['organization'], leafAll: ['employee'] },
  'organization.commission': { all: ['organization'], leafAll: ['commission'] },
  'record.survey': {
    leafAll: ['record'],
    leafForbid: ['termination', 'callback', 'operation', 'login'],
  },
  'survey.station.dashboard': {
    all: ['survey'],
    leafAny: [['dashboard', 'board', 'databoard']],
    leafForbid: ['analysis'],
  },
  'survey.station.analysis': { all: ['survey'], leafAll: ['analysis'] },
  'survey.station.member': {
    all: ['survey'],
    leafAll: ['member'],
    leafForbid: ['group', 'level', 'finance', 'log', 'bill'],
  },
  'survey.station.member.group': { all: ['survey', 'member'], leafAll: ['group'] },
  'survey.station.member.level': { all: ['survey', 'member'], leafAll: ['level'] },
  'survey.station.project.schedule': { all: ['survey'], leafAll: ['schedule'] },
  'survey.station.settings': { all: ['survey'], leafAny: [['settings', 'setting', 'config']] },
}

function recordSemanticSets(record: BackendGuideRouteRecord) {
  const leaf = new Set([
    ...splitTokens(record.path),
    ...splitTokens(record.component),
    ...splitTokens(record.name),
    ...splitTokens(record.title),
  ])
  const all = new Set([
    ...leaf,
    ...splitTokens(record.fullPath),
    ...record.parentPaths.flatMap(splitTokens),
    ...record.parentTitles.flatMap(splitTokens),
  ])
  return { leaf, all }
}

function semanticMatches(token: string, tokenSet: Set<string>) {
  return [...expandToken(token)].some(candidate => tokenSet.has(candidate))
}

function everySemantic(tokens: string[] | undefined, tokenSet: Set<string>) {
  return !tokens?.length || tokens.every(token => semanticMatches(token, tokenSet))
}

function everySemanticGroup(groups: string[][] | undefined, tokenSet: Set<string>) {
  return !groups?.length || groups.every(group => group.some(token => semanticMatches(token, tokenSet)))
}

function noneSemantic(tokens: string[] | undefined, tokenSet: Set<string>) {
  return !tokens?.length || tokens.every(token => !semanticMatches(token, tokenSet))
}

function isSurveyRoute(record: BackendGuideRouteRecord) {
  const { all } = recordSemanticSets(record)
  return semanticMatches('survey', all)
}

function ruleMatches(record: BackendGuideRouteRecord, rule: PageSemanticRule) {
  const { leaf, all } = recordSemanticSets(record)
  return everySemantic(rule.all, all)
    && everySemantic(rule.leafAll, leaf)
    && everySemanticGroup(rule.any, all)
    && everySemanticGroup(rule.leafAny, leaf)
    && noneSemantic(rule.forbid, all)
    && noneSemantic(rule.leafForbid, leaf)
}

function genericScore(record: BackendGuideRouteRecord, pageKey: string) {
  const keyTokens = pageKeyTokens(pageKey)
  const { primary, secondary } = routeTokenSets(record)
  let matched = 0
  let score = 0
  keyTokens.forEach((token) => {
    if (tokenMatches(token, primary)) {
      matched += 1
      score += DOMAIN_TOKENS.has(token) ? 48 : 34
    }
    else if (tokenMatches(token, secondary)) {
      matched += 1
      score += DOMAIN_TOKENS.has(token) ? 28 : 18
    }
  })
  const coverage = matched / Math.max(keyTokens.length, 1)
  return { score: coverage >= 0.999 ? score + 100 : 0, coverage }
}

function scoreRoute(record: BackendGuideRouteRecord, pageKey: string, editionId: EditionId) {
  const rule = PAGE_KEY_RULES[pageKey]
  let score = 0
  let coverage = 1

  if (rule) {
    if (!ruleMatches(record, rule)) return { score: 0, coverage: 0 }
    score = 600
    const semanticCount = [
      ...(rule.all || []),
      ...(rule.leafAll || []),
      ...(rule.any || []).flat(),
      ...(rule.leafAny || []).flat(),
    ].length
    score += semanticCount * 18
    score += rule.preferShallow ? Math.max(0, 30 - record.depth * 3) : record.depth * 2
  }
  else {
    const generic = genericScore(record, pageKey)
    score = generic.score
    coverage = generic.coverage
    if (!score) return { score: 0, coverage }
  }

  const surveyRoute = isSurveyRoute(record)
  const sampleFinance = editionId === 'sample-library'
    && /^finance\.(member|employee)\./.test(pageKey)
  const nonSampleFinance = editionId !== 'sample-library' && /^finance\./.test(pageKey)

  if (sampleFinance) score += surveyRoute ? 240 : -240
  if (nonSampleFinance) score += surveyRoute ? -160 : 120
  if (/^organization\./.test(pageKey)) score += surveyRoute ? -180 : 140
  if (pageKey === 'project.list') score += surveyRoute ? -180 : 140
  if (/^survey\./.test(pageKey)) score += surveyRoute ? 180 : -180

  if (pageKey === 'record.survey') {
    score += editionId === 'sample-library'
      ? (surveyRoute ? 220 : -220)
      : (surveyRoute ? -160 : 140)
  }

  return { score, coverage }
}

function normalizeRouteField(value: unknown) {
  return String(value || '')
    .replace(/\\/g, '/')
    .replace(/\/{2,}/g, '/')
    .trim()
    .toLowerCase()
}

/**
 * 供应商网络运营版的“辅助工具－数据字典”只允许使用
 * routeList 返回的 /otherFunctions/datadictionaryconfig 记录。
 *
 * 后端同时返回 /config/datadictionary 时，不再进入通用语义评分，
 * 避免两个同组件、同标题页面被判定为 ambiguous。
 */
function resolveSupplierAuxiliaryDictionaryRoute(
  records: BackendGuideRouteRecord[],
  pageKey: string,
  editionId: EditionId,
): GuideRouteResolution | null {
  if (editionId !== 'supplier-network' || pageKey !== 'auxiliary.dictionary') {
    return null
  }

  const exactCandidates = records.filter((record) => {
    return normalizePath(record.fullPath).toLowerCase()
      === '/otherfunctions/datadictionaryconfig'
      && normalizeRouteField(record.name) === 'datadictionaryconfig'
      && normalizeRouteField(record.component) === 'config/dictionary/index.vue'
      && String(record.title || '').trim() === '数据字典'
  })

  if (!exactCandidates.length) {
    return { status: 'not-found', record: null, candidates: [] }
  }

  if (exactCandidates.length > 1) {
    return {
      status: 'ambiguous',
      record: null,
      candidates: exactCandidates.slice(0, 5).map(record => ({
        record,
        score: 1000,
        coverage: 1,
      })),
    }
  }

  return {
    status: 'matched',
    record: exactCandidates[0],
    candidates: [{ record: exactCandidates[0], score: 1000, coverage: 1 }],
  }
}

/**
 * 根据业务 pageKey 在本次 routeList 接口记录中严格匹配页面。
 * 供应商网络运营版的辅助数据字典步骤按后端指定记录精确匹配；
 * 其余版本和步骤继续使用原有通用语义匹配逻辑。
 * 若第一名不唯一则返回 ambiguous，绝不猜测跳转。
 */
export function resolveGuideRouteRecordDetailed(
  records: BackendGuideRouteRecord[],
  pageKey: string,
  editionId: EditionId,
): GuideRouteResolution {
  if (!pageKey || pageKey === 'workspace') {
    return { status: 'not-found', record: null, candidates: [] }
  }

  const supplierDictionaryResolution = resolveSupplierAuxiliaryDictionaryRoute(
    records,
    pageKey,
    editionId,
  )
  if (supplierDictionaryResolution) {
    return supplierDictionaryResolution
  }

  const candidates = records
    .map((record) => {
      const result = scoreRoute(record, pageKey, editionId)
      return { record, ...result }
    })
    .filter(item => item.score > 0 && item.coverage >= 0.999)
    .sort((a, b) => b.score - a.score)

  if (!candidates.length) {
    return { status: 'not-found', record: null, candidates: [] }
  }

  const first = candidates[0]
  const second = candidates[1]
  if (second && first.score - second.score < 12) {
    return { status: 'ambiguous', record: null, candidates: candidates.slice(0, 5) }
  }

  return {
    status: 'matched',
    record: first.record,
    candidates: candidates.slice(0, 5),
  }
}

export function resolveGuideRouteRecord(
  records: BackendGuideRouteRecord[],
  pageKey: string,
  editionId: EditionId,
) {
  return resolveGuideRouteRecordDetailed(records, pageKey, editionId).record
}

export function resolveGuideRoute(
  records: BackendGuideRouteRecord[],
  pageKey: string,
  editionId: EditionId,
) {
  return resolveGuideRouteRecord(records, pageKey, editionId)?.fullPath || null
}
